import type { PaymentAdapter } from '@payloadcms/plugin-ecommerce/types'
import type { CollectionSlug } from 'payload'

/**
 * Bank Transfer Payment Adapter
 *
 * This adapter handles manual bank transfer payments where the customer:
 * 1. Places the order
 * 2. Transfers money to our bank account manually
 * 3. Admin verifies payment and updates order status
 *
 * Since this is a manual payment method, there's no payment processor involved.
 * We simply create the order and transaction records.
 */
export const bankTransferAdapter: PaymentAdapter = {
  name: 'bankTransfer',
  label: 'Bank Transfer',

  /**
   * For bank transfer, "initiating payment" just means preparing the transaction record.
   * No actual payment processing happens at this stage.
   */
  initiatePayment: async ({
    data: { cart, currency, customerEmail, billingAddress, shippingAddress },
    req,
    transactionsSlug,
  }) => {
    // Create a transaction record for tracking
    const transaction = await req.payload.create({
      collection: transactionsSlug as CollectionSlug,
      data: {
        currency,
        amount: cart.subtotal || 0,
        status: 'pending',
        paymentMethod: 'bankTransfer',
      } as any,
    })

    return {
      message: 'Bank transfer initiated. Please transfer the amount to complete your order.',
      transactionID: transaction.id,
    }
  },

  /**
   * Confirm the order for bank transfer payments.
   * Creates the order record with 'pending' status until payment is verified.
   */
  confirmOrder: async ({
    data,
    req,
    ordersSlug = 'orders',
    transactionsSlug = 'transactions',
    cartsSlug = 'carts',
    customersSlug = 'users',
  }) => {
    const {
      customerEmail,
      billingAddress,
      shippingAddress,
      emailConsent,
      cartID,
      // Extract passed totals from frontend
      orderSubtotal,
      orderTotal,
      shippingCost,
      bulkVolumeDiscount,
      manualCouponDiscount,
      couponId,
      orderLocale,
      ...additionalData
    } = data

    // Debug logging
    req.payload.logger.info(
      { cartID, customerEmail, hasUser: !!req.user },
      'BankTransfer confirmOrder called',
    )

    // Get the user from the request (if authenticated)
    const user = req.user

    // Get the cart - first try by cartID from data, then by customer
    let cart: any

    if (cartID) {
      // Fetch cart by ID passed from the endpoint
      req.payload.logger.info({ cartID, cartsSlug }, 'Fetching cart by ID')
      cart = await req.payload.findByID({
        collection: cartsSlug as CollectionSlug,
        id: cartID as string,
        depth: 2,
        overrideAccess: true, // Allow fetching cart even if user is not logged in
      })
      req.payload.logger.info(
        { cartFound: !!cart, itemCount: cart?.items?.length },
        'Cart fetch result',
      )
    } else if (user) {
      // Fall back to finding cart by customer
      req.payload.logger.info({ userId: user.id }, 'Fetching cart by customer')
      const cartsResult = await req.payload.find({
        collection: cartsSlug as CollectionSlug,
        where: {
          customer: { equals: user.id },
        },
        limit: 1,
        depth: 2, // Get product details
        overrideAccess: true,
      })
      cart = cartsResult.docs[0]
      req.payload.logger.info(
        { cartFound: !!cart, itemCount: cart?.items?.length },
        'Cart fetch by customer result',
      )
    }

    if (!cart || !cart.items || cart.items.length === 0) {
      req.payload.logger.error({ cart: !!cart, items: cart?.items }, 'Cart validation failed')
      throw new Error('No cart found or cart is empty')
    }

    // Generate a unique receipt ID (format: STM-YYYYMMDD-XXXXX)
    const now = new Date()
    const dateStr = now.toISOString().slice(0, 10).replace(/-/g, '')
    const randomPart = Math.floor(Math.random() * 100000)
      .toString()
      .padStart(5, '0')
    const receiptId = `STM-${dateStr}-${randomPart}`

    // Use passed totals if available, otherwise fall back to cart subtotal
    const finalTotal = orderTotal ?? cart.subtotal ?? 0
    const finalSubtotal = orderSubtotal ?? cart.subtotal ?? 0
    const finalShippingCost = shippingCost ?? 0
    const finalBulkDiscount = bulkVolumeDiscount ?? 0
    const finalCouponDiscount = manualCouponDiscount ?? 0
    const finalCouponId = couponId ?? null

    // Create a transaction record
    const transaction = await req.payload.create({
      collection: transactionsSlug as CollectionSlug,
      data: {
        currency: cart.currency || 'EUR',
        amount: finalTotal,
        status: 'pending', // Will be updated to 'succeeded' when admin verifies payment
        paymentMethod: 'bankTransfer',
      } as any,
      overrideAccess: true, // Allow creating transaction without auth
    })

    // Build order items from cart items
    const orderItems = cart.items.map((item: any) => {
      const product = typeof item.product === 'string' ? item.product : item.product?.id
      const variant = item.variant
        ? typeof item.variant === 'string'
          ? item.variant
          : item.variant?.id
        : undefined

      // Calculate item price
      let price = 0

      if (item.selectedVariantData?.priceTotal != null) {
        price = Math.round(Number(item.selectedVariantData.priceTotal) * 100)
      }

      if (price === 0 && typeof item.product === 'object' && item.product) {
        // First try: variant document from the variants collection (ecommerce plugin pattern)
        if (typeof item.variant === 'object' && item.variant) {
          const variantPrice = (item.variant as any).priceInEUR || (item.variant as any).price
          if (variantPrice != null) {
            price = Math.round(Number(variantPrice) * 100)
          }
        }
        // Second try: embedded variants on the product (legacy pattern)
        if (price === 0 && item.selectedVariants && item.product.variants) {
          // Try to get price from selected variant
          for (const variantDef of item.product.variants) {
            const selectedValue = item.selectedVariants?.[variantDef.name]
            if (selectedValue) {
              const valueObj = variantDef.values?.find((v: any) => v.value === selectedValue)
              if (valueObj?.price !== null && valueObj?.price !== undefined) {
                price = Math.round(Number(valueObj.price) * 100) // Convert to cents
                break
              }
            }
          }
        }
        // Fall back to base price
        if (price === 0 && item.product.basePrice) {
          price = Math.round(Number(item.product.basePrice) * 100)
        }
      }

      return {
        product,
        quantity: item.quantity,
        price,
        // Bake the product's custom tax rate into the order item
        tax_rate: typeof item.product === 'object' && item.product
          ? (item.product as any).customTaxRate ?? undefined
          : undefined,
        ...(variant ? { variant } : {}),
        ...(item.selectedVariants ? { selectedVariants: item.selectedVariants } : {}),
        ...(item.selectedVariantData ? { selectedVariantData: item.selectedVariantData } : {}),
      }
    })

    // Create the order
    // Get customer email from billing address or user
    const orderCustomerEmail = billingAddress?.email || customerEmail || user?.email

    const order = await req.payload.create({
      collection: ordersSlug as CollectionSlug,
      data: {
        receiptId,
        customer: user?.id, // Plugin uses 'customer' not 'orderedBy'
        customerEmail: orderCustomerEmail, // Plugin expects customerEmail field
        items: orderItems,
        amount: finalTotal, // Use the calculated total (includes shipping, discounts)
        total: finalTotal,
        currency: cart.currency || 'EUR',
        status: 'pending',
        paymentMethod: 'bankTransfer',
        transaction: transaction.id,
        orderLocale: (orderLocale as string) || 'en',
        shippingCost: finalShippingCost,
        // Store shipping info
        shipping: {
          shippingCost: finalShippingCost,
        },
        // Store discount info
        discounts: {
          discountAmount: finalBulkDiscount + finalCouponDiscount,
          bulkVolumeDiscount: finalBulkDiscount,
          manualCouponDiscount: finalCouponDiscount,
          appliedCouponId: finalCouponId,
        },
        ...(billingAddress ? { billingAddress } : {}),
        ...(shippingAddress ? { shippingAddress: shippingAddress || billingAddress } : {}),
        ...additionalData,
      } as any,
      overrideAccess: true, // Allow creating order without auth
    })

    // Clear the cart after successful order creation
    if (cart.id) {
      await req.payload.update({
        collection: cartsSlug as CollectionSlug,
        id: cart.id,
        data: {
          items: [],
        } as any,
        overrideAccess: true, // Allow updating cart to clear it (override potential user check)
      })
    }

    return {
      message:
        'Order created successfully. Please complete the bank transfer to process your order.',
      orderID: order.id,
      transactionID: transaction.id,
      receiptId,
    }
  },

  /**
   * Group field for admin panel - empty for now to avoid migration issues.
   * Can be extended later with fields like transferReference, transferVerifiedAt.
   */
  group: {
    name: 'bankTransfer',
    type: 'group',
    admin: {
      condition: (data) => data?.paymentMethod === 'bankTransfer',
    },
    fields: [],
  },

  endpoints: [],
}
